import { cloneElement, Suspense, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';

import { getUriExtension } from 'lib/utils/url';
import LottiePictograph from 'verify/components/LottiePictograph';
import ThemedSvg from 'verify/components/ThemedSvg';

const HIDE_PICTOGRAPH = 'none';

const ImageContainer = styled.div<{ height?: string }>`
  ${({ height }) => height != null && `height: ${height};`}

  > img, svg {
    ${({ height }) => height != null && `height: ${height};`}
  }
`;

// This is very verbose, but is the only way to make it type safe
interface CustomizablePictographProps {
  url:
    | 'idBackPictographUrl'
    | 'idFrontPictographUrl'
    | 'passportFrontPictographUrl'
    | 'passportSignaturePictographUrl'
    | 'governmentIdSelectPictographUrl'
    | 'documentPictographUrl'
    | 'selfiePictographUrl'
    | 'selfieCenterPictographUrl'
    | 'selfieLeftPictographUrl'
    | 'selfieRightPictographUrl'
    | 'failedPictographUrl'
    | 'successPictographUrl'
    | 'exitPictographUrl'
    | 'loadingPictographUrl'
    | 'cameraSupportPictographUrl';
  height?:
    | 'idBackPictographHeight'
    | 'idFrontPictographHeight'
    | 'passportFrontPictographHeight'
    | 'passportSignaturePictographHeight'
    | 'governmentIdSelectPictographHeight'
    | 'documentPictographHeight'
    | 'selfiePictographHeight'
    | 'selfieCenterPictographHeight'
    | 'selfieLeftPictographHeight'
    | 'selfieRightPictographHeight'
    | 'failedPictographHeight'
    | 'successPictographHeight'
    | 'exitPictographHeight'
    | 'loadingPictographHeight'
    | 'cameraSupportPictographHeight';
  children?: JSX.Element;
  className?: string;
  vectorEffect?: 'initial' | 'non-scaling-stroke';
  'aria-label'?: string;
  /**
   * Special handling for icons like the verify checkmark that should NOT be themed.
   */
  themed?: boolean;
  primaryColor?: string;
  highlightColor?: string;
  backgroundColor?: string;
  strokeColor?: string;
}

/**
 * Displays remote image specified in theme if it exists. Else, uses child.
 * Note that the remote image must be the correct dimensions.
 */
const CustomizablePictograph = ({
  url,
  children,
  className,
  height,
  vectorEffect,
  'aria-label': ariaLabel,
  themed = true,
  primaryColor,
  highlightColor,
  backgroundColor,
  strokeColor,
}: CustomizablePictographProps) => {
  const theme = useContext(ThemeContext);

  const assetUrl = theme?.[url];

  // support hiding any customizable pictograph
  if (assetUrl === HIDE_PICTOGRAPH) {
    return null;
  }

  if (!assetUrl) {
    if (children == null) {
      return null;
    }
    const clonedChild = cloneElement(children, { 'aria-label': ariaLabel });
    // Only use ThemedSvg to wrap built-in assets.
    return themed ? (
      <ThemedSvg
        vectorEffect={vectorEffect}
        className={className}
        $primaryColor={primaryColor}
        $highlightColor={highlightColor}
        $backgroundColor={backgroundColor}
        $strokeColor={strokeColor}
      >
        {clonedChild}
      </ThemedSvg>
    ) : (
      <div className={className}>{clonedChild}</div>
    );
  }

  switch (getUriExtension(assetUrl)) {
    case 'png':
    case 'svg':
    case 'jpg':
    case 'gif':
      return (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <ImageContainer height={theme[height]}>
          <img src={assetUrl} aria-label={ariaLabel} alt={ariaLabel} />
        </ImageContainer>
      );
    case 'json':
      // lottie file
      return (
        <Suspense fallback={null}>
          <LottiePictograph
            assetUrl={assetUrl}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            height={theme[height]}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            fallback={children != null && cloneElement(children, { 'aria-label': ariaLabel })}
            aria-label={ariaLabel}
          />
        </Suspense>
      );
    default:
      throw new Error(
        `Invalid pictograph ${theme[url]}. Valid extensions are: .png, .svg, .jpg, .gif, .json.`,
      );
  }
};

export default CustomizablePictograph;
